import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { PushNotifications } from '@capacitor/push-notifications';
import { Share } from '@capacitor/share';

const DEVICE_ID_STORAGE_KEY = 'localally_native_device_id';
const PUSH_TOKEN_STORAGE_KEY = 'localally_native_push_token';

const getRandomId = () => {
  const randomUuid = (window.crypto as Crypto & { randomUUID?: () => string })?.randomUUID?.();
  return randomUuid || `device_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

export const isNativeMobileRuntime = () => Capacitor.isNativePlatform();

export const getNativeDeviceId = () => {
  const existing = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
  if (existing) return existing;
  const next = getRandomId();
  localStorage.setItem(DEVICE_ID_STORAGE_KEY, next);
  return next;
};

export const getStoredNativePushToken = () => localStorage.getItem(PUSH_TOKEN_STORAGE_KEY) || undefined;

export const buildNativeDevicePayload = (pushToken: string) => ({
  deviceId: getNativeDeviceId(),
  platform: Capacitor.getPlatform() === 'ios' ? 'ios' as const : 'android' as const,
  pushToken,
  appVersion: process.env.REACT_APP_VERSION || '1.0.0',
  locale: navigator.language || 'en-IN',
  enabled: true,
});

export const registerNativePushNotifications = async () => {
  if (!isNativeMobileRuntime()) {
    return { status: 'unsupported' as const, message: 'Native notifications are available in the Android app.' };
  }

  const permissions = await PushNotifications.requestPermissions();
  if (permissions.receive !== 'granted') {
    return { status: 'denied' as const, message: 'Notification permission was not granted.' };
  }

  const pushToken = await new Promise<string>(async (resolve, reject) => {
    let registrationHandle: { remove: () => Promise<void> } | undefined;
    let errorHandle: { remove: () => Promise<void> } | undefined;
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

    const cleanup = () => {
      if (timeoutHandle) clearTimeout(timeoutHandle);
      void registrationHandle?.remove();
      void errorHandle?.remove();
    };

    timeoutHandle = setTimeout(() => {
      cleanup();
      reject(new Error('Timed out while registering this device for notifications.'));
    }, 15000);

    registrationHandle = await PushNotifications.addListener('registration', (token) => {
      cleanup();
      resolve(token.value);
    });
    errorHandle = await PushNotifications.addListener('registrationError', (error) => {
      cleanup();
      reject(new Error(String(error?.error || 'Notification registration failed.')));
    });

    await PushNotifications.register();
  });

  localStorage.setItem(PUSH_TOKEN_STORAGE_KEY, pushToken);

  return {
    status: 'registered' as const,
    payload: buildNativeDevicePayload(pushToken),
  };
};

export const pickNativeProfileImage = async (): Promise<string | null> => {
  if (!isNativeMobileRuntime()) return null;

  const photo = await Camera.getPhoto({
    quality: 82,
    allowEditing: false,
    resultType: CameraResultType.DataUrl,
    source: CameraSource.Prompt,
    width: 1400,
    height: 1400,
    correctOrientation: true,
  });

  if (!photo.dataUrl) {
    throw new Error('No image was selected.');
  }

  return photo.dataUrl;
};

export const shareLocalAllyLink = async (payload: {
  title: string;
  text: string;
  url: string;
}) => {
  if (isNativeMobileRuntime()) {
    await Share.share({
      title: payload.title,
      text: payload.text,
      url: payload.url,
      dialogTitle: payload.title,
    });
    return 'shared';
  }

  if (navigator.share) {
    await navigator.share(payload);
    return 'shared';
  }

  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(payload.url);
    return 'copied';
  }

  return 'unavailable';
};
